import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import BookingWidget from "./BookingWidget";
import Navbar from "./Navbar";
import { supabase } from "@/integrations/supabase/client";

const HeroSection = () => {
  const [slides, setSlides] = useState<string[]>([]);
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    supabase
      .from("hero_slides")
      .select("image_url")
      .eq("is_active", true)
      .order("sort_order")
      .then(({ data }) => {
        if (data && data.length > 0) setSlides(data.map((d) => d.image_url));
      });
  }, []);

  useEffect(() => {
    if (slides.length <= 1) return;
    const t = setInterval(() => setCurrent((c) => (c + 1) % slides.length), 6000);
    return () => clearInterval(t);
  }, [slides.length]);

  return (
    <section className="relative min-h-[85vh] pt-20 overflow-visible">
      <Navbar />

      {/* Background slider */}
      {slides.length > 0 && (
        <AnimatePresence mode="wait">
          <motion.div
            key={current}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 1.2 }}
            className="absolute inset-0 z-0"
          >
            <img src={slides[current]} alt="" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-foreground/40" />
          </motion.div>
        </AnimatePresence>
      )}

      {slides.length === 0 && (
        <div className="absolute inset-0 z-0 bg-primary" />
      )}

      {/* Centered content */}
      <div className="relative z-10 container mx-auto px-4 flex flex-col items-center justify-center text-center" style={{ minHeight: "calc(85vh - 5rem)" }}>
        <motion.h1
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.6 }}
          className="font-display text-4xl md:text-5xl lg:text-6xl font-bold text-white leading-tight mb-5 max-w-4xl"
        >
          Book The Best Tours and Transfers
          <br />
          <span className="text-secondary">in Punta Cana</span>
        </motion.h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.5 }}
          className="text-white/80 text-lg md:text-xl max-w-2xl mb-8 leading-relaxed"
        >
          Unique experiences, comfortable rides, and safe journeys to make the most of every destination
        </motion.p>
      </div>

      {/* Slide indicators */}
      {slides.length > 1 && (
        <div className="absolute z-10 bottom-28 left-1/2 -translate-x-1/2 flex gap-2">
          {slides.map((_, i) => (
            <button
              key={i}
              onClick={() => setCurrent(i)}
              className={`h-1.5 rounded-none transition-all duration-300 ${
                i === current ? "bg-secondary w-8" : "bg-white/40 w-3"
              }`}
            />
          ))}
        </div>
      )}

      {/* Booking widget overlapping bottom */}
      <div className="relative z-20 container mx-auto px-4 md:-mt-10 -mt-4 pb-8" id="booking">
        <BookingWidget />
      </div>
    </section>
  );
};

export default HeroSection;
